// ui/tomeModal.js
// Tome of Echoes modal UI - displays discovered items collection + bestiary

import { getDiscoveryStats, getAllCategoryStats, searchItems, isDiscovered } from '../systems/tomeOfEchoes.js';
import { getItemName, getItemIcon, getItemColor } from '../data/items/index.js';
import { formatNumber } from '../utils/format.js';
import { getBestiaryStats, getAllZoneBestiaryStats, searchMobs, getMobFullDetails } from '../systems/bestiary.js';
import { BOUNTY_MARKS } from '../skills/bounty/bountyData.js';
import { getMobLoot } from '../skills/combat/combatData.js';

// Tab state
const TOME_TAB_KEY = 'idleRealms_tome_tab';
let currentTomeTab = localStorage.getItem(TOME_TAB_KEY) || 'items';

let modalElement = null;
const hdPrefetchCache = new Set(); // Persists across modal open/close
let searchFilter = '';
let collapsedCategories = new Set();
let searchDebounceTimer = null;
let zoomedItem = null;

// ==================== SHARED HELPERS ====================

/**
 * Format quantity for display
 */
function formatQuantity(quantity) {
    if (quantity >= 10000) {
        return formatNumber(quantity);
    }
    return quantity.toLocaleString();
}

// ==================== ITEMS TAB ====================

/**
 * Render a single item slot
 */
function renderItemSlot(item) {
    const icon = getItemIcon(item.id, null, { skipAtlas: true });
    const color = getItemColor(item.id);
    const name = item.discovered ? getItemName(item.id) : '???';
    const obtainedText = item.discovered ? `Obtained: ${formatQuantity(item.obtainedCount)}` : 'Not yet discovered';

    const discoveredClass = item.discovered ? 'discovered' : 'undiscovered';

    const bgStyle = item.discovered
        ? `background-color: ${color};`
        : 'background-color: #2a2a3a;';

    const iconStyle = item.discovered
        ? ''
        : 'filter: grayscale(100%) opacity(0.4);';

    const quantityBadge = item.discovered
        ? `<span class="tome-slot-qty">${formatQuantity(item.obtainedCount)}</span>`
        : '';

    const dataItemId = item.discovered ? `data-item-id="${item.id}"` : '';

    const clickHandler = item.discovered
        ? `onclick="window.tomeModal.zoomItem('${item.id}', ${item.obtainedCount})"`
        : '';

    return `
        <div class="tome-slot ${discoveredClass}"
             ${dataItemId}
             ${clickHandler}
             data-tooltip="${name}\n${obtainedText}">
            <div class="tome-slot-content" style="${bgStyle}">
                <span class="tome-item-icon" style="${iconStyle}">${icon}</span>
                ${quantityBadge}
            </div>
        </div>
    `;
}

/**
 * Render a category section
 */
function renderCategorySection(categoryStats) {
    const isCollapsed = collapsedCategories.has(categoryStats.id);
    const collapsedClass = isCollapsed ? 'collapsed' : '';
    const toggleIcon = isCollapsed ? '▶' : '▼';
    const isComplete = categoryStats.discovered === categoryStats.total;
    const completeClass = isComplete ? 'complete' : '';

    const itemsHtml = isCollapsed
        ? ''
        : `<div class="tome-grid">
            ${categoryStats.items.map(item => renderItemSlot(item)).join('')}
           </div>`;

    return `
        <div class="tome-category ${collapsedClass} ${completeClass}">
            <div class="tome-category-header" onclick="window.tomeModal.toggleCategory('${categoryStats.id}')">
                <span class="category-toggle">${toggleIcon}</span>
                <span class="category-icon">${categoryStats.icon}</span>
                <span class="category-name">${categoryStats.name}</span>
                <span class="category-count">${categoryStats.discovered}/${categoryStats.total}</span>
                ${isComplete ? '<span class="category-complete-icon">✓</span>' : ''}
            </div>
            ${itemsHtml}
        </div>
    `;
}

/**
 * Render item search results
 */
function renderSearchResults(results) {
    if (results.length === 0) {
        return '<div class="tome-no-results">No items found matching your search.</div>';
    }

    return `
        <div class="tome-search-results">
            <div class="tome-grid">
                ${results.map(item => renderItemSlot(item)).join('')}
            </div>
        </div>
    `;
}

/**
 * Render the items completion header
 */
function renderItemsCompletionHeader() {
    const stats = getDiscoveryStats();
    return `
        <div class="tome-completion">
            <div class="tome-completion-text">
                <span class="tome-discovered-count">${stats.discovered}</span>
                <span class="tome-total-count">/ ${stats.total} items discovered</span>
                <span class="tome-percentage">(${stats.percentage}%)</span>
            </div>
            <div class="tome-progress-bar">
                <div class="tome-progress-fill" style="width: ${stats.percentage}%"></div>
            </div>
        </div>
    `;
}

/**
 * Render items tab body content
 */
function renderItemsBodyContent() {
    if (searchFilter.trim().length > 0) {
        const searchResults = searchItems(searchFilter);
        return renderSearchResults(searchResults);
    } else {
        const categoryStats = getAllCategoryStats();
        return `
            <div class="tome-categories">
                ${categoryStats.map(cat => renderCategorySection(cat)).join('')}
            </div>
        `;
    }
}

// ==================== BESTIARY TAB ====================

/**
 * Mob icon map (duplicated from combatUI to avoid cross-dependency)
 */
const MOB_ICONS = {
    // Tier 1 - Goblin Camp
    goblin_raider: '👺', goblin_shaman: '🧙', goblin_skirmisher: '👺',
    camp_chicken: '🐔', goblin_herbalist: '🌿', ember_goblin: '🔥',
    goblin_trapper: '🪤', goblin_looter: '💰', goblin_firestarter: '🧨',
    // Tier 1 - Forgotten Outskirts
    scavenger: '🥷', stray_mercenary: '⚔️', feral_beast: '🐺',
    carrion_hawk: '🦅', ruined_sentry: '🗿', marsh_forager: '🐸', wild_hound: '🐕',
    // Tier 1 - Bandit Hideout
    bandit_thug: '👤', bandit_archer: '🏹', bandit_enforcer: '🥷',
    bandit_alchemist: '⚗️', black_market_runner: '🏃', bandit_scout: '👁️',
    bandit_smuggler: '🎒', bandit_hexer: '🧿',
    // Tier 2 - Troll Caves
    cave_troll: '👹', troll_brute: '👹', regenerating_troll: '👹',
    crystal_lurker: '💎', ashbound_troll: '🔥', fungal_crawler: '🍄',
    stone_golem: '🪨', cave_spider: '🕷️',
    // Tier 2 - Undead Crypt
    skeleton_warrior: '💀', rotting_zombie: '🧟', crypt_acolyte: '☠️',
    tomb_weaver: '🕸️', scorched_revenant: '🔥', spectral_archer: '👻',
    bone_collector: '🦴', cursed_knight: '⚔️',
    // Tier 2 - Demon Scar
    lesser_demon: '👿', flame_imp: '🔥', scarred_cultist: '🧛',
    infernal_arcanist: '🔮', cinderbound_cultist: '🔥', pit_fiend: '😈',
    soul_harvester: '👻', hellhound: '🐕‍🦺',
    // Tier 3 - Dragon Lair
    wyrmling: '🐲', ancient_dragon: '🐉', draconic_sentinel: '🛡️',
    hoard_guardian: '🛡️', molten_wyrm: '🌋', drake_whelp: '🐉',
    dragon_priest: '🧙', scaled_marauder: '🦎',
    // Tier 3 - Void Rift
    void_aberration: '👁️', rift_horror: '🌀', void_channeler: '💜',
    rift_weaver: '🕸️', void_ash_horror: '💀', void_stalker: '👁️',
    rift_scavenger: '🦇', entropy_maw: '🌀',
    // Tier 3 - Ancient Sanctum
    elder_construct: '🗿', corrupted_avatar: '👤', elder_god_fragment: '⚡',
    elder_herbkeeper: '🧓', sanctum_guardian: '🗿', ancient_scribe: '📜',
    temporal_shade: '⏳', relic_hoarder: '🏺',
    // Ashen Crucible
    ashen_warden: '🔥', pyre_guardian: '🛡️', flame_eternal: '🌋',
    searing_matriarch: '👑', molten_colossus: '🗿', cindered_knight: '⚔️',
    convergence_herald: '🌀'
};

function getBestiaryMobIcon(mob) {
    if (!mob) return '👾';
    return MOB_ICONS[mob.id] || '👾';
}

function formatDropRate(chance) {
    if (!chance || chance <= 0) return '';
    if (chance >= 1) return 'Always';
    const oneIn = Math.round(1 / chance);
    if (oneIn <= 1) return 'Always';
    return `1/${oneIn}`;
}

function getStyleIcon(style) {
    const icons = { melee: '⚔️', ranged: '🏹', magic: '✨' };
    return icons[style] || '❓';
}

function getStyleName(style) {
    if (!style) return 'None';
    return style.charAt(0).toUpperCase() + style.slice(1);
}

/**
 * Get mechanic description text
 */
function getMechanicDescription(mechanic) {
    if (!mechanic) return '';
    const descriptions = {
        snare: `Slows your attack speed by ${Math.round(mechanic.slowPercent * 100)}% for ${mechanic.duration / 1000}s (${Math.round(mechanic.chance * 100)}% chance)`,
        fireDot: `Burns for ${mechanic.damage} damage every ${mechanic.interval / 1000}s for ${mechanic.duration / 1000}s`,
        poison: `Poisons for ${mechanic.damage} damage every ${mechanic.interval / 1000}s for ${mechanic.duration / 1000}s`,
        curse: `Reduces defense by ${mechanic.defenseReduction} for ${mechanic.duration / 1000}s (${Math.round(mechanic.chance * 100)}% chance)`,
        selfHeal: `Heals ${mechanic.healAmount} HP on hit (${Math.round(mechanic.chance * 100)}% chance)`,
        enrage: `Attacks ${Math.round(mechanic.speedBoost * 100)}% faster below ${Math.round(mechanic.hpThreshold * 100)}% HP`,
        phaseShift: `${Math.round(mechanic.chance * 100)}% chance to dodge attacks`,
        absorption: `Heals ${Math.round(mechanic.healPercent * 100)}% of damage dealt (cap: ${mechanic.healCap})`,
        timeWarp: `Attack speed varies ${mechanic.minSpeed / 1000}s - ${mechanic.maxSpeed / 1000}s`,
        damageReflection: `Reflects ${Math.round(mechanic.reflectPercent * 100)}% of damage taken (cap: ${mechanic.reflectCap})`,
        armorShred: `Shreds ${mechanic.defensePerStack} defense per stack (max ${mechanic.maxStacks}) for ${mechanic.duration / 1000}s`
    };
    return descriptions[mechanic.type] || mechanic.type;
}

/**
 * Render bestiary completion header
 */
function renderBestiaryCompletionHeader() {
    const stats = getBestiaryStats();
    return `
        <div class="tome-completion">
            <div class="tome-completion-text">
                <span class="tome-discovered-count">${stats.discovered}</span>
                <span class="tome-total-count">/ ${stats.total} mobs discovered</span>
                <span class="tome-percentage">(${stats.percentage}%)</span>
            </div>
            <div class="tome-progress-bar">
                <div class="tome-progress-fill" style="width: ${stats.percentage}%"></div>
            </div>
        </div>
    `;
}

/**
 * Render a single mob card in the bestiary grid
 */
function renderMobCard(mob) {
    if (!mob) return '';

    if (!mob.discovered) {
        return `
            <div class="bestiary-card undiscovered" data-tooltip="???\nNot yet discovered">
                <div class="bestiary-card-icon">?</div>
                <div class="bestiary-card-name">???</div>
            </div>
        `;
    }

    const icon = getBestiaryMobIcon(mob);
    const killText = `${formatQuantity(mob.killCount)} kill${mob.killCount !== 1 ? 's' : ''}`;

    // Bounty mark badge
    let markBadge = '';
    const mark = mob.bountyMark || { rank: 0 };
    if (mark.rank > 0 && mark.title) {
        markBadge = `<span class="bestiary-mark-badge">${mark.title}</span>`;
    }

    return `
        <div class="bestiary-card discovered"
             onclick="window.tomeModal.showMobDetail('${mob.id}')"
             data-tooltip="${mob.name}\nHP: ${mob.hp} | XP: ${mob.xp}\n${killText}">
            ${markBadge}
            <div class="bestiary-card-icon">${icon}</div>
            <div class="bestiary-card-name">${mob.name}</div>
            <div class="bestiary-card-stats">
                <span>HP ${mob.hp}</span>
                <span>XP ${mob.xp}</span>
            </div>
            <div class="bestiary-card-kills">${killText}</div>
        </div>
    `;
}

/**
 * Render a zone section in the bestiary
 */
function renderZoneBestiarySection(zoneStats) {
    const isCollapsed = collapsedCategories.has('zone_' + zoneStats.id);
    const collapsedClass = isCollapsed ? 'collapsed' : '';
    const toggleIcon = isCollapsed ? '▶' : '▼';
    const isComplete = zoneStats.discovered === zoneStats.total;
    const completeClass = isComplete ? 'complete' : '';

    let gridContent = '';
    if (!isCollapsed) {
        if (zoneStats.tiers) {
            // Tiered zone (Ashen Crucible) - render sub-tier headers
            gridContent = zoneStats.tiers.map(tier => {
                const tierMobs = zoneStats.mobs.filter(m => tier.mobIds.includes(m.id));
                return `
                    <div class="bestiary-subtier-header">${tier.name}</div>
                    ${tierMobs.map(mob => renderMobCard(mob)).join('')}
                `;
            }).join('');
        } else {
            gridContent = zoneStats.mobs.map(mob => renderMobCard(mob)).join('');
        }

        gridContent = `<div class="bestiary-grid">${gridContent}</div>`;
    }

    return `
        <div class="tome-category ${collapsedClass} ${completeClass}">
            <div class="tome-category-header" onclick="window.tomeModal.toggleCategory('zone_${zoneStats.id}')">
                <span class="category-toggle">${toggleIcon}</span>
                <span class="bestiary-zone-tier tier-${zoneStats.tier}">T${zoneStats.tier}</span>
                <span class="category-name">${zoneStats.name}</span>
                <span class="bestiary-zone-level">Lv ${zoneStats.level}</span>
                <span class="category-count">${zoneStats.discovered}/${zoneStats.total}</span>
                ${isComplete ? '<span class="category-complete-icon">✓</span>' : ''}
            </div>
            ${gridContent}
        </div>
    `;
}

/**
 * Render bestiary search results
 */
function renderBestiarySearchResults(results) {
    if (results.length === 0) {
        return '<div class="tome-no-results">No mobs found matching your search.</div>';
    }

    // Only show discovered mobs in search results
    const discoveredResults = results.filter(m => m.discovered);
    if (discoveredResults.length === 0) {
        return '<div class="tome-no-results">No discovered mobs match your search.</div>';
    }

    return `
        <div class="tome-search-results">
            <div class="bestiary-grid">
                ${discoveredResults.map(mob => renderMobCard(mob)).join('')}
            </div>
        </div>
    `;
}

/**
 * Render bestiary tab body content
 */
function renderBestiaryBodyContent() {
    if (searchFilter.trim().length > 0) {
        const results = searchMobs(searchFilter);
        return renderBestiarySearchResults(results);
    } else {
        const zoneStats = getAllZoneBestiaryStats();
        return `
            <div class="tome-categories">
                ${zoneStats.map(zone => renderZoneBestiarySection(zone)).join('')}
            </div>
        `;
    }
}

/**
 * Show mob detail overlay
 */
function showMobDetail(mobId) {
    const details = getMobFullDetails(mobId);
    if (!details) return;

    zoomedItem = mobId;
    const icon = getBestiaryMobIcon(details);

    // Combat stats section
    const statsHtml = `
        <div class="bestiary-section">
            <div class="bestiary-section-label">Combat Stats</div>
            <div class="bestiary-section-content">
                <div class="bestiary-stats-grid">
                    <div class="bestiary-stat-item">
                        <span class="bestiary-stat-label">HP</span>
                        <span class="bestiary-stat-value">${details.hp}</span>
                    </div>
                    <div class="bestiary-stat-item">
                        <span class="bestiary-stat-label">ATK</span>
                        <span class="bestiary-stat-value">${details.attack}-${details.attackMax}</span>
                    </div>
                    <div class="bestiary-stat-item">
                        <span class="bestiary-stat-label">DEF</span>
                        <span class="bestiary-stat-value">${details.defense}</span>
                    </div>
                    <div class="bestiary-stat-item">
                        <span class="bestiary-stat-label">ACC</span>
                        <span class="bestiary-stat-value">${details.accuracy}</span>
                    </div>
                    <div class="bestiary-stat-item">
                        <span class="bestiary-stat-label">SPD</span>
                        <span class="bestiary-stat-value">${(details.attackSpeed / 1000).toFixed(1)}s</span>
                    </div>
                    <div class="bestiary-stat-item">
                        <span class="bestiary-stat-label">XP</span>
                        <span class="bestiary-stat-value">${details.xp}</span>
                    </div>
                </div>
            </div>
        </div>
    `;

    // Weakness / Resistance section
    let wrContent = '';
    if (details.weaknessRevealed) {
        const weakText = details.weakTo
            ? `${getStyleIcon(details.weakTo)} ${getStyleName(details.weakTo)}`
            : 'None';
        const weakClass = details.weakTo ? 'weak' : 'none';
        wrContent += `
            <div class="bestiary-wr-row">
                <span class="bestiary-wr-label">Weak to:</span>
                <span class="bestiary-wr-value ${weakClass}">${weakText}</span>
            </div>
        `;
    } else {
        wrContent += `
            <div class="bestiary-wr-row">
                <span class="bestiary-wr-label">Weak to:</span>
                <span class="research-locked">??? Research required</span>
            </div>
        `;
    }

    if (details.resistanceRevealed) {
        const resistText = details.resistantTo
            ? `${getStyleIcon(details.resistantTo)} ${getStyleName(details.resistantTo)}`
            : 'None';
        const resistClass = details.resistantTo ? 'resist' : 'none';
        wrContent += `
            <div class="bestiary-wr-row">
                <span class="bestiary-wr-label">Resistant to:</span>
                <span class="bestiary-wr-value ${resistClass}">${resistText}</span>
            </div>
        `;
    } else {
        wrContent += `
            <div class="bestiary-wr-row">
                <span class="bestiary-wr-label">Resistant to:</span>
                <span class="research-locked">??? Research required</span>
            </div>
        `;
    }

    const wrHtml = `
        <div class="bestiary-section">
            <div class="bestiary-section-label">Weakness / Resistance</div>
            <div class="bestiary-section-content">${wrContent}</div>
        </div>
    `;

    // Mechanic section (only if mob has one)
    let mechanicHtml = '';
    if (details.mechanic) {
        const mechDesc = getMechanicDescription(details.mechanic);
        const mechType = details.mechanic.type.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase());
        mechanicHtml = `
            <div class="bestiary-section">
                <div class="bestiary-section-label">Special Mechanic</div>
                <div class="bestiary-section-content">
                    <div class="bestiary-mechanic">
                        <span class="bestiary-mechanic-type">${mechType}</span>
                        <span class="bestiary-mechanic-desc">${mechDesc}</span>
                    </div>
                </div>
            </div>
        `;
    }

    // Bounty mark section
    const mark = details.bountyMark;
    const nextMark = details.nextMark;
    let markContent = '';
    if (mark.rank > 0) {
        markContent += `<div class="bestiary-mark-rank">${mark.title}</div>`;
        const bonuses = [];
        if (mark.bountyBonus > 0) bonuses.push(`+${Math.round(mark.bountyBonus * 100)}% Contract Rewards`);
        if (mark.dropRate > 0) bonuses.push(`+${(mark.dropRate * 100).toFixed(1)}% Drop Rate`);
        if (mark.accuracy > 0) bonuses.push(`+${mark.accuracy} Accuracy vs Mob`);
        if (mark.defIgnore > 0) bonuses.push(`+${mark.defIgnore} Defense Ignored`);
        if (bonuses.length > 0) {
            markContent += `<div class="bestiary-mark-bonuses">${bonuses.map(b => `<span class="bestiary-mark-bonus">${b}</span>`).join('')}</div>`;
        }
    } else {
        markContent += `<div class="bestiary-mark-rank" style="color: #6a5a8a;">No Mark</div>`;
    }

    if (nextMark) {
        const progress = Math.min(100, Math.round((mark.killCount / nextMark.kills) * 100));
        markContent += `
            <div class="bestiary-mark-progress">
                <div class="bestiary-mark-progress-fill" style="width: ${progress}%"></div>
            </div>
            <div class="bestiary-mark-progress-text">${formatQuantity(mark.killCount)} / ${formatQuantity(nextMark.kills)} kills to ${nextMark.title}</div>
        `;
    } else if (mark.rank === BOUNTY_MARKS.length - 1) {
        markContent += `<div class="bestiary-mark-progress-text" style="color: #d4af37;">Max rank achieved!</div>`;
    }

    const markHtml = `
        <div class="bestiary-section">
            <div class="bestiary-section-label">Bounty Mark</div>
            <div class="bestiary-section-content">${markContent}</div>
        </div>
    `;

    // Loot table section
    let lootContent = '';
    const hasCommon = details.loot.common.length > 0;
    const hasRare = details.loot.rare.length > 0;

    if (hasCommon || hasRare) {
        const renderLootItem = (drop, isRare) => {
            const icon = getItemIcon(drop.itemId, null, { skipAtlas: true });
            const name = drop.itemDiscovered ? getItemName(drop.itemId) : '???';
            const color = drop.itemDiscovered ? getItemColor(drop.itemId) : '#2a2a3a';
            const discoveredClass = drop.itemDiscovered ? '' : 'undiscovered';
            const rareClass = isRare ? 'rare' : '';
            const qtyText = drop.minQty === drop.maxQty ? `${drop.minQty}` : `${drop.minQty}-${drop.maxQty}`;
            const rateText = formatDropRate(drop.chance);
            const obtainedText = drop.itemDiscovered ? `\nObtained: ${formatQuantity(drop.totalObtained)}` : '';
            const tooltip = `${name}\nQty: ${qtyText} | Rate: ${rateText}${obtainedText}`;

            return `
                <div class="bestiary-loot-item ${discoveredClass} ${rareClass}"
                     style="background-color: ${color};"
                     data-tooltip="${tooltip}">
                    <span class="tome-item-icon">${icon}</span>
                </div>
            `;
        };

        lootContent = '<div class="bestiary-loot-grid">';
        if (hasCommon) {
            lootContent += `<div class="bestiary-loot-label">Common</div>`;
            lootContent += details.loot.common.map(d => renderLootItem(d, false)).join('');
        }
        if (hasRare) {
            lootContent += `<div class="bestiary-loot-label">Rare</div>`;
            lootContent += details.loot.rare.map(d => renderLootItem(d, true)).join('');
        }
        lootContent += '</div>';
    } else {
        lootContent = '<div class="tome-no-results">No known drops</div>';
    }

    const lootHtml = `
        <div class="bestiary-section">
            <div class="bestiary-section-label">Loot Table</div>
            <div class="bestiary-section-content">${lootContent}</div>
        </div>
    `;

    // Zone info
    const zoneName = details.zone ? details.zone.name : 'Unknown';

    // Build the overlay
    const overlay = document.createElement('div');
    overlay.id = 'tome-zoom-overlay';
    overlay.className = 'tome-zoom-overlay';
    overlay.onclick = (e) => {
        if (e.target === overlay) closeZoom();
    };

    overlay.innerHTML = `
        <div class="bestiary-detail-content">
            <div class="bestiary-detail-header">
                <div class="bestiary-detail-icon">${icon}</div>
                <div>
                    <div class="bestiary-detail-name">${details.name}</div>
                    <div class="bestiary-detail-zone">${zoneName}</div>
                </div>
                <button class="bestiary-detail-close" onclick="window.tomeModal.closeZoom()">&times;</button>
            </div>
            <div class="bestiary-detail-body">
                ${statsHtml}
                ${wrHtml}
                ${mechanicHtml}
                ${markHtml}
                ${lootHtml}
            </div>
            <div class="bestiary-total-kills">
                Total Kills: <strong>${formatQuantity(details.killCount)}</strong>
            </div>
        </div>
    `;

    document.body.appendChild(overlay);

    requestAnimationFrame(() => {
        overlay.classList.add('visible');
    });
}

// ==================== TAB SYSTEM ====================

/**
 * Render the tab bar
 */
function renderTabBar() {
    return `
        <div class="tome-tab-bar">
            <button class="tome-tab ${currentTomeTab === 'items' ? 'active' : ''}"
                    onclick="window.tomeModal.switchTab('items')">Items</button>
            <button class="tome-tab ${currentTomeTab === 'bestiary' ? 'active' : ''}"
                    onclick="window.tomeModal.switchTab('bestiary')">Bestiary</button>
        </div>
    `;
}

/**
 * Render completion header based on current tab
 */
function renderCompletionHeader() {
    if (currentTomeTab === 'bestiary') {
        return renderBestiaryCompletionHeader();
    }
    return renderItemsCompletionHeader();
}

/**
 * Render body content based on current tab
 */
function renderBodyContent() {
    if (currentTomeTab === 'bestiary') {
        return renderBestiaryBodyContent();
    }
    return renderItemsBodyContent();
}

/**
 * Switch between tabs
 */
function switchTab(tabId) {
    currentTomeTab = tabId;
    localStorage.setItem(TOME_TAB_KEY, tabId);
    searchFilter = '';
    if (searchDebounceTimer) clearTimeout(searchDebounceTimer);

    if (!modalElement) return;

    // Update tab bar active states without full modal rebuild
    const tabButtons = modalElement.querySelectorAll('.tome-tab');
    tabButtons.forEach(btn => {
        const isActive = btn.textContent.trim().toLowerCase() === tabId;
        btn.classList.toggle('active', isActive);
    });

    // Update completion header
    const completionHeader = modalElement.querySelector('.tome-completion');
    if (completionHeader) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = renderCompletionHeader();
        const newHeader = tempDiv.querySelector('.tome-completion');
        if (newHeader) completionHeader.replaceWith(newHeader);
    }

    // Update search placeholder
    const searchInput = modalElement.querySelector('.tome-search');
    if (searchInput) {
        searchInput.placeholder = currentTomeTab === 'bestiary' ? 'Search mobs...' : 'Search echoes...';
        searchInput.value = '';
    }
    // Clear the X button if visible
    const clearBtn = modalElement.querySelector('.tome-search-clear');
    if (clearBtn) clearBtn.remove();

    // Update content area only
    updateModalContent();
}

// ==================== MODAL CORE ====================

/**
 * Render the full modal content
 */
function renderModalContent() {
    const searchPlaceholder = currentTomeTab === 'bestiary' ? 'Search mobs...' : 'Search echoes...';

    return `
        <div class="modal-overlay" onclick="window.tomeModal.handleOverlayClick(event)">
            <div class="modal-content tome-modal">
                <div class="modal-header">
                    <span class="tome-header-icon"></span>
                    <h2>Tome of Echoes</h2>
                    <button class="modal-close" onclick="window.tomeModal.close()">&times;</button>
                </div>
                <div class="modal-body">
                    ${renderTabBar()}
                    ${renderCompletionHeader()}
                    <div class="tome-search-container">
                        <input type="text"
                               class="tome-search"
                               placeholder="${searchPlaceholder}"
                               value="${searchFilter}"
                               oninput="window.tomeModal.handleSearch(this.value)">
                        ${searchFilter ? '<button class="tome-search-clear" onclick="window.tomeModal.clearSearch()">×</button>' : ''}
                    </div>
                    <div class="tome-content-area">
                        ${renderBodyContent()}
                    </div>
                </div>
            </div>
        </div>
    `;
}

/**
 * Open the Tome modal
 */
export function openTomeModal() {
    searchFilter = '';
    collapsedCategories.clear();

    modalElement = document.createElement('div');
    modalElement.id = 'tome-modal-container';
    modalElement.innerHTML = renderModalContent();
    document.body.appendChild(modalElement);

    // Prefetch HD images on hover so they're cached before zoom click
    // Uses mouseover (bubbles) instead of mouseenter (doesn't bubble)
    modalElement.addEventListener('mouseover', (e) => {
        const slot = e.target.closest('.tome-slot.discovered');
        if (!slot) return;
        const spriteEl = slot.querySelector('.sprite-icon[data-hd-src]');
        if (spriteEl) {
            const hdSrc = spriteEl.dataset.hdSrc;
            if (!hdPrefetchCache.has(hdSrc)) {
                hdPrefetchCache.add(hdSrc);
                const img = new Image();
                img.src = hdSrc;
            }
        }
    });

    // Focus search input
    const searchInput = modalElement.querySelector('.tome-search');
    if (searchInput) {
        searchInput.focus();
    }

    // Add keyboard listener
    document.addEventListener('keydown', handleKeyDown);
}

/**
 * Close the Tome modal
 */
export function closeTomeModal() {
    if (modalElement) {
        modalElement.remove();
        modalElement = null;
    }
    document.removeEventListener('keydown', handleKeyDown);
}

/**
 * Update only the content area (not the whole modal) to reduce flicker
 */
function updateModalContent() {
    if (!modalElement) return;

    const contentArea = modalElement.querySelector('.tome-content-area');
    if (contentArea) {
        const scrollContainer = modalElement.querySelector('.modal-body');
        const scrollTop = scrollContainer ? scrollContainer.scrollTop : 0;

        contentArea.innerHTML = renderBodyContent();

        if (scrollContainer) {
            scrollContainer.scrollTop = scrollTop;
        }
    }
}

/**
 * Update the modal content without recreating it (full re-render)
 */
function updateModal() {
    if (!modalElement) return;

    const container = modalElement.querySelector('.modal-content');
    if (container) {
        const scrollContainer = modalElement.querySelector('.modal-body');
        const scrollTop = scrollContainer ? scrollContainer.scrollTop : 0;

        modalElement.innerHTML = renderModalContent();

        const newScrollContainer = modalElement.querySelector('.modal-body');
        if (newScrollContainer) {
            newScrollContainer.scrollTop = scrollTop;
        }

        const searchInput = modalElement.querySelector('.tome-search');
        if (searchInput && searchFilter.length > 0) {
            searchInput.focus();
            searchInput.setSelectionRange(searchFilter.length, searchFilter.length);
        }
    }
}

/**
 * Handle search input with debouncing
 */
function handleSearch(value) {
    searchFilter = value;

    if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
    }

    searchDebounceTimer = setTimeout(() => {
        updateModalContent();
    }, 100);
}

/**
 * Clear search
 */
function clearSearch() {
    searchFilter = '';
    if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
    }
    updateModal();
    const searchInput = modalElement?.querySelector('.tome-search');
    if (searchInput) {
        searchInput.focus();
    }
}

/**
 * Toggle category collapse state
 */
function toggleCategory(categoryId) {
    if (collapsedCategories.has(categoryId)) {
        collapsedCategories.delete(categoryId);
    } else {
        collapsedCategories.add(categoryId);
    }
    updateModalContent();
}

/**
 * Handle overlay click (close on background click)
 */
function handleOverlayClick(event) {
    if (event.target.classList.contains('modal-overlay')) {
        closeTomeModal();
    }
}

/**
 * Handle keyboard events
 */
function handleKeyDown(event) {
    if (event.key === 'Escape') {
        if (zoomedItem) {
            closeZoom();
        } else {
            closeTomeModal();
        }
    }
}

/**
 * Show zoomed view of an item
 */
function zoomItem(itemId, obtainedCount) {
    zoomedItem = itemId;

    const icon = getItemIcon(itemId, null, { skipAtlas: true });
    const name = getItemName(itemId);
    const color = getItemColor(itemId);

    const zoomOverlay = document.createElement('div');
    zoomOverlay.id = 'tome-zoom-overlay';
    zoomOverlay.className = 'tome-zoom-overlay';
    zoomOverlay.onclick = (e) => {
        if (e.target === zoomOverlay) {
            closeZoom();
        }
    };

    zoomOverlay.innerHTML = `
        <div class="tome-zoom-content" style="background-color: ${color};">
            <div class="tome-zoom-icon">${icon}</div>
            <div class="tome-zoom-name">${name}</div>
            <div class="tome-zoom-count">Lifetime obtained: ${formatNumber(obtainedCount)}</div>
            <div class="tome-zoom-hint">Scroll to zoom</div>
        </div>
    `;

    document.body.appendChild(zoomOverlay);

    // Swap atlas sprite to HD original for zoom quality
    const spriteEl = zoomOverlay.querySelector('.sprite-icon[data-hd-src]');
    if (spriteEl) {
        const hdSrc = spriteEl.dataset.hdSrc;
        const applyHd = () => {
            spriteEl.style.backgroundImage = `url('${hdSrc}')`;
            spriteEl.style.backgroundPosition = '0px 0px';
            spriteEl.style.backgroundSize = 'contain';
        };

        if (hdPrefetchCache.has(hdSrc)) {
            // Already prefetched — browser has it cached, swap immediately
            applyHd();
        } else {
            // Not prefetched — load then swap
            hdPrefetchCache.add(hdSrc);
            const hdImg = new Image();
            hdImg.onload = applyHd;
            hdImg.src = hdSrc;
        }
    }

    let zoomLevel = 1;
    const MIN_ZOOM = 0.5;
    const MAX_ZOOM = 3;
    const ZOOM_STEP = 0.15;
    const content = zoomOverlay.querySelector('.tome-zoom-content');

    zoomOverlay.addEventListener('wheel', (e) => {
        e.preventDefault();
        zoomLevel = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoomLevel + (e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP)));
        content.style.transform = `scale(${zoomLevel})`;
    }, { passive: false });

    requestAnimationFrame(() => {
        zoomOverlay.classList.add('visible');
    });
}

/**
 * Close the zoom overlay
 */
function closeZoom() {
    const zoomOverlay = document.getElementById('tome-zoom-overlay');
    if (zoomOverlay) {
        zoomOverlay.classList.remove('visible');
        setTimeout(() => {
            zoomOverlay.remove();
        }, 200);
    }
    zoomedItem = null;
}

/**
 * Update the Tome discovery count in the sidebar and header
 */
export function updateTomeButtonCount() {
    const stats = getDiscoveryStats();
    const progressText = `${stats.discovered}/${stats.total}`;

    const countElement = document.getElementById('tome-count');
    if (countElement) {
        countElement.textContent = progressText;
    }

    const headerTome = document.getElementById('header-tome-progress');
    if (headerTome) {
        headerTome.textContent = progressText;
    }
}

// Expose functions to window for onclick handlers
window.tomeModal = {
    open: openTomeModal,
    close: closeTomeModal,
    handleSearch,
    clearSearch,
    toggleCategory,
    handleOverlayClick,
    zoomItem,
    closeZoom,
    switchTab,
    showMobDetail
};

export default {
    openTomeModal,
    closeTomeModal,
    updateTomeButtonCount
};
